/**
 * The admin session.
 *
 * Three states that a single boolean would blur: signed out, password
 * accepted but the emailed code not yet entered, and ready. Collapsing them
 * produces a dead end where the user is told "no" with nothing to act on.
 *
 * There is no client-side auth library any more. The session lives in an
 * httpOnly cookie the browser sends automatically, so this hook's whole job is
 * to ask the server who we are and cache the answer.
 */

import { createContext, useContext, useEffect, useState, useCallback } from 'react';

import { api, ApiError } from '../lib/api.js';

export interface AdminMe {
  email: string;
  displayName: string | null;
  role: 'admin' | 'viewer';
  assuranceLevel: string;
  canRevealBankDetails: boolean;
}

export type AdminState =
  | { status: 'loading' }
  | { status: 'signed-out' }
  /** Password accepted, but the emailed code has not been entered yet. */
  | { status: 'challenge'; me: AdminMe }
  | { status: 'ready'; me: AdminMe };

function classify(me: AdminMe): AdminState {
  // A session that has not used the emailed code is signed in only far enough
  // to enter one, so it gets the sign-in screen rather than the app.
  if (me.assuranceLevel !== 'aal2') return { status: 'challenge', me };
  return { status: 'ready', me };
}

export function useAdminSessionState(): {
  state: AdminState;
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
} {
  const [state, setState] = useState<AdminState>({ status: 'loading' });

  const refresh = useCallback(async () => {
    try {
      setState(classify(await api.get<AdminMe>('/api/admin/me')));
    } catch (error) {
      // 401 is the ordinary signed-out case, not a failure worth surfacing.
      if (error instanceof ApiError && error.status === 401) {
        setState({ status: 'signed-out' });
        return;
      }
      throw error;
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const signOut = useCallback(async () => {
    await api.post('/api/admin/logout').catch(() => {
      // Even if the call fails, stop showing the app.
    });
    setState({ status: 'signed-out' });
  }, []);

  return { state, refresh, signOut };
}

export const AdminSessionContext = createContext<{
  me: AdminMe;
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
} | null>(null);

export function useAdmin() {
  const context = useContext(AdminSessionContext);
  if (!context) throw new Error('useAdmin must be used inside the admin shell');
  return context;
}
